import { useMemo } from 'react'

import { Text } from '@wartek-id/text-v1'

import {
  isSelectableTugasPokok,
  resolvePerencanaanReviewDiff,
} from 'app/KinerjaGuruPage/utils/perencanaan'
import { IndikatorPerencanaanPanel } from 'app/KinerjaShared/components/IndikatorPerencanaanPanel'
import { PerencanaanReviewDiff } from 'app/KinerjaShared/components/PerencanaanReviewDiff'

import type { PerencanaanItemReviewDiff } from 'app/KinerjaGuruPage/types/perencanaan'
import type { KinerjaUtamaDetail } from 'types/kinerja'

export type TugasPokokItemsPros = {
  originalIds: Array<number>
  reviewIds: Array<number>
  showReview: boolean
  contents: Array<KinerjaUtamaDetail>
  orderNumber: number
}

export function TugasPokokItems({
  originalIds,
  reviewIds,
  showReview,
  contents,
  orderNumber,
}: TugasPokokItemsPros) {
  // Content as map for constant time lookup
  const contentsMap: Record<number, KinerjaUtamaDetail> = useMemo(() => {
    return contents.reduce((acc, content) => {
      acc[content.id] = content
      return acc
    }, {} as Record<number, KinerjaUtamaDetail>)
  }, [contents])

  // 2. Resolve the mandatory and selectable items
  // 3. Map the items to the content
  const {
    mandatoryItems,
    selectableItems,
  }: {
    mandatoryItems: Array<PerencanaanItemReviewDiff<number>>
    selectableItems: Array<PerencanaanItemReviewDiff<number>>
  } = useMemo(() => {
    // 1. Resolve the review and original items
    let items: Array<PerencanaanItemReviewDiff<number>> = []

    if (showReview) {
      items = resolvePerencanaanReviewDiff(originalIds, reviewIds)
    } else {
      items = originalIds.map((id) => ({
        isChanged: false,
        original: id,
        review: null,
      }))
    }

    // 2. Resolve the mandatory and selectable items
    const mandatoryItems = []
    const selectableItems = []

    items.forEach((item) => {
      const isSelectable = isSelectableTugasPokok(
        contentsMap[showReview ? item.review : item.original]?.tag
      )

      if (isSelectable) {
        selectableItems.push(item)
      } else {
        mandatoryItems.push(item)
      }
    })

    return { mandatoryItems, selectableItems }
  }, [originalIds, reviewIds, contentsMap, showReview])

  return (
    <>
      {/* Mandatory Tugas Pokok */}
      {mandatoryItems.map((item, index) => (
        <IndikatorPerencanaanPanel
          key={`tugas-pokok-mandatory-${index}`}
          variant="static"
          orderNumber={index + 1}
          title={
            showReview
              ? contentsMap[item.review]?.rencanaHasilKerja
              : contentsMap[item.original]?.rencanaHasilKerja
          }
        />
      ))}

      {selectableItems.map((item, index) => {
        const resolvedOrderNumber = `${orderNumber + mandatoryItems.length}.${
          index + 1
        }`

        const hasOriginal = !!item.original
        const shouldShowReview = showReview && item.isChanged

        return (
          <IndikatorPerencanaanPanel
            key={`tugas-tambahan-${index}`}
            variant="static"
            orderNumber={resolvedOrderNumber}
            upperContent={
              shouldShowReview && (
                <PerencanaanReviewDiff
                  originalLabel="Perencanaan Guru"
                  reviewLabel="Penyesuaian dari Kepala Sekolah"
                  originalContent={
                    hasOriginal && (
                      <IndikatorPerencanaanPanel
                        variant="static"
                        orderNumber={resolvedOrderNumber}
                      >
                        <Text>
                          Terlaksananya pembimbingan dan pelatihan dalam
                          mewujudkan pembelajaran yang bermutu untuk semua
                          berdasarkan pilihan&nbsp;
                          <strong>
                            {contentsMap[item.original]?.targetKualitas}
                          </strong>
                        </Text>
                      </IndikatorPerencanaanPanel>
                    )
                  }
                  reviewContent={
                    <IndikatorPerencanaanPanel
                      variant="static"
                      orderNumber={resolvedOrderNumber}
                    >
                      <Text>
                        Terlaksananya pembimbingan dan pelatihan dalam
                        mewujudkan pembelajaran yang bermutu untuk semua
                        berdasarkan pilihan&nbsp;
                        <strong>
                          {contentsMap[item.review]?.targetKualitas}
                        </strong>
                      </Text>
                    </IndikatorPerencanaanPanel>
                  }
                />
              )
            }
          >
            <Text>
              Terlaksananya pembimbingan dan pelatihan dalam mewujudkan
              pembelajaran yang bermutu untuk semua berdasarkan pilihan&nbsp;
              <strong>
                {
                  contentsMap[showReview ? item.review : item.original]
                    ?.targetKualitas
                }
              </strong>
            </Text>
          </IndikatorPerencanaanPanel>
        )
      })}
    </>
  )
}
